/**
 * TESTING: The PDP JS is the most complex set of methods in the project.
 * So if you make any changes to the file you'll need to follow the following testing sheet (URL)
 */

import { formatMoney } from "@shopify/theme-currency";
import { getUrlWithVariant, ProductForm } from "@shopify/theme-product-form";
import { register } from "@shopify/theme-sections";
import Selection from "../models/product-selection";
import getRetailPrice from "../models/get-retail-price";
import * as Cart from "../utils/cart";
import BackInStockForm from "../components/back-in-stock-form";
import ProductImages from "../components/product-images";
import ProductSlider from "../components/product-slider";
import ProductDescription from "../components/product-description";
import { getColourOption, selectors } from "../utils/product";
import App from "../../vue/App.vue";
import { createApp } from "vue";
import i18n from "./i18n";

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const classes = {
  discounted: "product-price--discounted",
  hide: "u-hide",
  sizeDisabled: "radio-button--disabled",
  fixedActionVisible: "product-actions__action--fixed--visible",
  fadeIn: "fade-in",
  visible: "visible",
  loading: "loading",
};

register("template-product", {
  async onLoad() {
    /**
     * DOM Selectors
     */
    this.debouncedUpdateSizeAltSnippet = debounce(this.updateSizeAltSnippet.bind(this), 300);
    this.$el = document.querySelector(selectors.productForm);
    this.$submit = this.$el.querySelector(selectors.submitButton);
    this.$productAction = this.$el.querySelector(selectors.productAction);
    this.$backInStockAction = this.$el.querySelector(
      selectors.backInStock.button
    );
    this.$productActionFixed = this.$el.querySelector(
      selectors.productActionFixed
    );
    this.$sizes = this.$el.querySelectorAll(selectors.size);
    this.$wishlistButton = this.$el.querySelector(selectors.wishListButton);
    this.$productDescription = this.$el.querySelector(
      selectors.description.body
    );
    this.$giftCardRecipientEmail = this.$el.querySelector(
      selectors.giftCard.recipientEmail
    );
    this.$giftCardRecipientName = this.$el.querySelector(
      selectors.giftCard.recipientName
    );
    this.$giftCardRecipientSendOn = this.$el.querySelector(
      selectors.giftCard.recipientSendOn
    );
    this.$giftCardMessage = this.$el.querySelector(selectors.giftCard.message);
    this.$productQuantities = this.$el.querySelector(selectors.productQuantity);
    this.$addedToCartScreenReaderMessage = this.$el.querySelector(
      selectors.screenReader.addedToCart
    );
    this.$addedToWishListScreenReaderMessage = this.$el.querySelector(
      selectors.screenReader.addedToWishlist
    );
    this.$backInStockForm = new BackInStockForm(
      document.querySelector(selectors.backInStock.form)
    );
    this.$recommendedProductSlider = document.querySelector(
      selectors.recommendedProductSlider
    );
    this.$video = document.querySelector("[data-product-video]");

    this.enableHistory = this.$el.dataset.enableHistoryState === "true";
    this.product = await this.getProductJson();

    this.variants = this.product.variants;
    this.quantity = 1;
    this.updatedVariant = false;
    if (this.$el.querySelector(selectors.retailPrice)) {
      this.retailPrice = Number(
        this.$el.querySelector(selectors.retailPrice).dataset.retailPrice
      );
    }
    this.productForm = new ProductForm(this.$el, this.product, {
      onFormSubmit: this.onFormSubmit.bind(this),
      onOptionChange: this.onFormOptionChange.bind(this),
      onQuantityChange: this.onFormQuantityChange.bind(this),
    });

    this.productImages = new ProductImages(
      document.querySelector(selectors.images.wrapper),
      this.product.media
    );

    this.selectedOptions = new Selection(this.product, this.productForm);

    this.selectedColour = getColourOption();

    if (this.isAlternateColourProduct()) {
      await this.loadAlternateColourVariants();
    }

    this.updateAvailableVariants();
    this.disableProductVariantButton();
    this.updateSizes();

    // Get the current variant and loading product recommendations on load.
    this.curentVariant = document.querySelector("[data-current]");

    let dataCurrent = this.curentVariant.getAttribute("data-current");
    dataCurrent = decodeURIComponent(dataCurrent);
    let currentV = JSON.parse(dataCurrent);
    let currentP = currentV.featured_image.product_id;

    this.getProductRecs(currentV, currentP);

    if (this.$wishlistButton) {
      this.$wishlistButton.addEventListener("click", () => {
        this.addItemToWishList();
      });
      this.checkIfProductIsAlreadyInTheWishlist();
    }

    if (this.$recommendedProductSlider) {
      // eslint-disable-next-line no-new
      new ProductSlider(this.$recommendedProductSlider);
    }
    if (this.$productDescription) {
      // eslint-disable-next-line no-new
      new ProductDescription();
    }
    this.toggleFixedProductAction();

    if (this.$video) {
      this.checkIfVideoHasLoaded(this.$video);
    }
  },

  updateBackInStockForm(product, variant) {
    const productInput = document.querySelector(selectors.backInStock.product)
    const variantInput = document.querySelector(selectors.backInStock.variant)
  
    if (productInput) {
      productInput.value = product.id;
      productInput.dataset.backInStockProduct = product.id;
    }
  
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.dataset.backInStockVariant = variant.id;
    }
  },

  isAlternateColourProduct() {
    const hasAlternateColours = !!this.product.metafields && !!this.product.metafields.custom && !!this.product.metafields.custom.alternate_colours;
    return hasAlternateColours;
  },

  async loadAlternateColourVariants() {
    const alternateColours = JSON.parse(this.product.metafields.custom.alternate_colours);
    const alternateVariantsPromises = alternateColours.map(colourProduct =>
        fetch(`/products/${colourProduct.handle}.js`).then(response => response.json())
    );

    const alternateProducts = await Promise.all(alternateVariantsPromises);
    this.alternateVariants = alternateProducts.flatMap(product => product.variants);
  },


  toggleFixedProductAction() {
    const boundingEl = document.querySelector(selectors.productActionsWrapper);
    const productActionElementHeightOffset = -Math.abs(
      boundingEl.getBoundingClientRect().height
    );
    document.addEventListener("scroll", () => {
      if (
        boundingEl.getBoundingClientRect().top <
          productActionElementHeightOffset ||
        boundingEl.getBoundingClientRect().top > window.innerHeight
      ) {
        document
          .querySelectorAll(selectors.productActionFixed)
          .forEach((el) => {
            el.classList.add(classes.fixedActionVisible);
          });
      } else {
        document
          .querySelectorAll(selectors.productActionFixed)
          .forEach((el) => {
            el.classList.remove(classes.fixedActionVisible);
          });
      }
    });
  },

  addItemToWishList() {
    if (this.updatedVariant) {
      window.SwymPageData.epi = this.updatedVariant.id;
      window.SwymPageData.variants = `[{${this.updatedVariant.title}:${this.updatedVariant.id}`;
      window.SwymPageData.iu = this.updatedVariant.featured_image.src;
    }
    window.SwymPageData.et = 4; // et 4 stands for wishlist event type;
    // eslint-disable-next-line no-underscore-dangle
    window._swat.addToWishList(window.SwymPageData, () => {
      this.$wishlistButton.querySelector("[data-wishlist-text]").innerHTML =
        "Added To Wishlist";
      this.$addedToWishListScreenReaderMessage.textContent = `Item Added To Wishlist`;
      this.$wishlistButton.setAttribute("disabled", true);
    });
  },

  checkIfProductIsAlreadyInTheWishlist() {
    // eslint-disable-next-line no-underscore-dangle
    if (typeof window._swat !== 'undefined' && typeof window._swat.fetch !== 'undefined') {
      window._swat.fetch((response) => {
        const results = response;

        const url = new URLSearchParams(window.location.search);

        let variantId = Number(url.get("variant"));
        if (this.updatedVariant) {
          variantId = this.updatedVariant.id;
        }
        const isProductInWishList = results.find((p) => {
          return p.empi === window.SwymPageData.empi && p.epi === variantId;
        });

        if (isProductInWishList) {
          this.$wishlistButton.setAttribute("disabled", true);
          this.$wishlistButton.querySelector("[data-wishlist-text]").innerHTML =
            "Added To Wishlist";
        } else {
          this.$wishlistButton.removeAttribute("disabled", true);
          this.$wishlistButton.querySelector("[data-wishlist-text]").innerHTML =
            "Add To Wishlist";
        }
      });
    } else {
        return false;
    }
  },

  async getProductJson() {
    const currentCountry = Shopify.country.toUpperCase();

    const { productHandle } = this.$el.dataset;
    const productByHandle = await fetch(`/products/${productHandle}.js`).then((response) =>
      response.json());

    const query = `
    query getProductInfo @inContext(country: ${currentCountry}) {
      node(id: "gid://shopify/Product/${productByHandle.id}") {
        ... on Product {
          id
          title
          handle
          description
          publishedAt
          createdAt
          vendor
          productType
          tags
          images(first: 10) {
            edges {
              node {
                url
                altText
              }
            }
          }
          metafield(namespace: "custom", key: "alternate_colours") {
            namespace
            key
            value
          }
          variants(first: 100) {
            edges {
              node {
                id
                title
                availableForSale
                selectedOptions {
                  name
                  value
                }
                image {
                  url
                }
              }
            }
          }
        }
      }
    }`;

    const requestDefaults = {
      async: true,
      crossDomain: true,
      method: "POST",
      headers: {
        "X-Shopify-Storefront-Access-Token": import.meta.env.VITE_SHOPIFY_STOREFRONT_API_KEY,
        "Content-Type": "application/graphql",
      },
    };

    const STOREFRONT_API_VERSION = "2024-04";
    const STOREFRONT_API_URL = `/api/${STOREFRONT_API_VERSION}/graphql.json`;

    const fetchFromStorefront = (body) => {
      return fetch(STOREFRONT_API_URL, {
        ...requestDefaults,
        body
      }).then((response) => response.json());
    };
    
    try {
        const response = await fetchFromStorefront(query);
        const { data } = response;

        if (!data || !data.node) {
            throw new Error('Product data not found');
        }

        //const product = data.node;
        //product.variants = product.variants.edges ? product.variants.edges.map(edge => edge.node) : [];
        //product.metafields = product.metafield ? { 'custom.alternate_colours': product.metafield.value } : {};
        
        const metafield = data.node.metafield ? { 'custom.alternate_colours': data.node.metafield.value } : {};

        const product = {
          ...productByHandle,
          metafields: metafield
        };

        return product;
    } catch (error) {
        console.error(`Error fetching product data: ${error}`);
    }
  },

  /**
   * Returns only the values for the option at the given index. This filters out variants
   * that don't match the currently selected options (barring the provided option) and
   * any variants that are currently unavailable to purchase.
   *
   * @param {Number} index
   */
  getAvailableValuesForOption(index) {

    return this.variants
      .filter((variant) => variant.available)
      .filter((variant) =>
        variant.options.every(
          (value, optionIndex) => {
            return optionIndex === index || value === this.selectedOptions.index(optionIndex)
          }
        )
      )
      .map((variant) => variant.options[index]);
  },

  onUnload() {
    this.productForm.destroy();
  },

  onFormQuantityChange(event) {
    this.quantity = event.dataset.quantity;
  },

  updateQuantitiesFromInventory(variant) {
    // reset the quantity state to 1 after changing variant.
    this.quantity = 1;

    // Get the quantity data from liquid and filters the quantity data to match the current variant.
    const variantQuantityData = JSON.parse(
      this.$productQuantities.dataset.productQuantity
    ).filter((currentVariant) => currentVariant.variant_id === variant.id);

    // Gets the parent node to hide the whole element if the product is out of stock.
    const { parentNode } = this.$productQuantities;

    // Hide the class if the variant is sold out.
    if (!variant.available) {
      parentNode.classList.add(classes.hide);
    } else {
      parentNode.classList.remove(classes.hide);
    }

    // Reset the options for the next variant.
    this.$productQuantities.innerHTML = "";

    // Gets the variant quantity and returns it, if it's more than 50, then it will return 50.
    const variantQuantity =
      variantQuantityData[0].variant_quantity <= 50
        ? variantQuantityData[0].variant_quantity
        : 50;

    for (let quantity = 1; quantity <= variantQuantity; quantity++) {
      const option = document.createElement("option");
      option.value = quantity;
      option.textContent = quantity;
      this.$productQuantities.append(option);
    }

    if (variantQuantity === 0) {
      this.$productQuantities.innerHTML = '<option value="0">N/A</option>';
    }
  },

  async createAndMountApp(recommendations, heading) {
    const mounted = document.querySelector("[data-app]");

    if (this.productRecs) {
      this.productRecs.unmount();
    }

    this.productRecs = createApp(App, { recommendations, heading });
    this.productRecs.use(i18n);
    this.productRecs.mount(mounted);
  },

  async getParentRecs(productId) {
    const currentCountry = Shopify.country.toUpperCase();
    const queryProductObject = `gid://shopify/Product/${productId}`;
    const queryProduct = `
    query getParentProductInfo @inContext(country: ${currentCountry}) {
      node(id: "${queryProductObject}") {
        ... on Product {
          id
          title
          metafield(key: "recommended_products", namespace: "products") {
            value
            id
            reference
          }
        }
      }
    }
    `;
  
    /** @type {RequestInit} */
    const requestDefaults = {
      async: true,
      crossDomain: true,
      method: "POST",
      headers: {
        "X-Shopify-Storefront-Access-Token": import.meta.env.VITE_SHOPIFY_STOREFRONT_API_KEY,
        "Content-Type": "application/graphql",
      },
    };
  
    const STOREFRONT_API_VERSION = "2024-04";
  
    const STOREFRONT_API_URL = `/api/${STOREFRONT_API_VERSION}/graphql.json`;
  
    const fetchFromStorefront = (body) => {
      return fetch(STOREFRONT_API_URL, {
        ...requestDefaults,
        body
      }).then((response) => response.json());
    };
  
    try {
      const storeFrontProduct = await fetchFromStorefront(queryProduct);
  
      if (!storeFrontProduct.data.node.metafield) {
        return { recommendations: [], productHeading: '' };
      }
  
      const productData = storeFrontProduct.data;
      const productMetaId = productData.node.metafield.value;
  
      const productMetaQuery = `
      {
        metaobject(id: "${productMetaId}") {
          handle
          id
          fields {
            key
            value
            reference
          }
        }
      }
      `;
  
      const productMetaDetails = await fetchFromStorefront(productMetaQuery);
      const productMetaData = productMetaDetails.data;
      const productHeading = productMetaData.metaobject.fields.find((field) => field.key === "heading")?.value || '';
  
      const variantIdsField = productMetaData.metaobject.fields.find((field) => field.key === "variants");
      const productsField = productMetaData.metaobject.fields.find((field) => field.key === "products");
  
      const variantIds = variantIdsField ? JSON.parse(variantIdsField.value) : [];
      const productIds = productsField ? JSON.parse(productsField.value) : [];

  
      if (!variantIds.length && !productIds.length) {
        return { recommendations: [], productHeading };
      }
  
      const variantQueries = variantIds.length > 0 ? variantIds.map((variant, index) => `
        q${index}: node(id: "${variant}") {
          ... on ProductVariant {
            id
            title
            quantityAvailable
            product {
              id
              handle
              title
              featuredImage {
                url
              }
              options {
                id
                name
                values
              }
            }
            selectedOptions {
              name
              value
            }
            price {
              amount
              currencyCode
            }
            image {
              src
            }
          }
        }
      `).join("\n") : '';
  
      const productQueries = productIds.length > 0 ? productIds.map((product, index) => `
        p${index}: node(id: "${product}") {
          ... on Product {
            id
            handle
            title
            featuredImage {
              url
            }
            options {
              name
              values
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  title
                  quantityAvailable
                  selectedOptions {
                    name
                    value
                  }
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    src
                  }
                }
              }
            }
            metafield(key: "alternate_colours", namespace: "custom") {
              value
              references(first: 10) {
                edges {
                  node {
                    ... on Product {
                      id
                      handle
                      title
                      featuredImage {
                        url
                      }
                      variants(first: 1) {
                        edges {
                          node {
                            id
                            title
                            quantityAvailable
                            selectedOptions {
                              name
                              value
                            }
                            price {
                              amount
                              currencyCode
                            }
                            image {
                              src
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `).join("\n") : '';
      
      const currentCountry = Shopify.country.toUpperCase();

      const metaQuery = `
      query getMetaInfo @inContext(country: ${currentCountry}) {
        ${variantQueries}
        ${productQueries}
      }
      `;

      const metaDetails = await fetchFromStorefront(metaQuery);
      const queriedMetaDetails = metaDetails.data;
  
      const variantRecommendationsArray = variantIds.length > 0 ? Object.values(queriedMetaDetails).filter(item => item.id.includes('/ProductVariant/')) : [];
      const productRecommendationsArray = productIds.length > 0 ? Object.values(queriedMetaDetails).filter(item => item.id.includes('/Product/')) : [];
  
      const productRecommendations = productRecommendationsArray.map(product => {
        const variant = product.variants.edges[0].node;
        const parentProductOptions = product.options.find(option => option.name.includes('Size'));
  
        let parentSizeOptions = [];
        if (parentProductOptions) {
          parentSizeOptions = parentProductOptions.values.map(sizeValue => {
            const parentIsOutOfStock = variant.selectedOptions.some(option => option.name === 'Size' && option.value === sizeValue) &&
              variant.quantityAvailable < 1;
  
            return {
              value: sizeValue,
              parentIsOutOfStock,
            };
          });
        }

        const alternateColours = product.metafield ? product.metafield.references.edges.map(({ node }) => ({
          id: node.id,
          handle: node.handle,
          title: node.title,
          featuredImage: node.featuredImage,
          variant: node.variants.edges[0].node,
        })) : [];

        return {
          ...variant,
          product: {
            id: product.id,
            handle: product.handle,
            title: product.title,
            featuredImage: product.featuredImage,
            options: product.options,
          },
          parentSizeOptions,
          alternateColours,
        };
      });

      const variantRecommendations = variantRecommendationsArray.map((variant) => {
        const parentProductOptions = variant.product.options.find(option => option.name.includes('Size'));
  
        let parentSizeOptions = [];
        if (parentProductOptions) {
          parentSizeOptions = parentProductOptions.values.map(sizeValue => {
            const parentIsOutOfStock = variant.selectedOptions.some(option => option.name === 'Size' && option.value === sizeValue) &&
              variant.quantityAvailable < 1;
  
            return {
              value: sizeValue,
              parentIsOutOfStock,
            };
          });
        }
  
        return {
          ...variant,
          parentSizeOptions,
        };
      });
  
      const recommendations = [
        ...productRecommendationsArray.length > 0 ? productRecommendations : [],
        ...variantRecommendationsArray.length > 0 ? variantRecommendations : []
      ];

      return { recommendations, productHeading };
    } catch (e) {
      console.error(e);
      return { recommendations: [], productHeading: '' };
    }
  },
  
  async getProductRecs(variant, productId) {
    const currentCountry = Shopify.country.toUpperCase();
    const queryVariantObject = `gid://shopify/ProductVariant/${variant.id}`;
    const queryVariant = `
    query getVariantInfo @inContext(country: ${currentCountry}) {
      node(id: "${queryVariantObject}") {
        ... on ProductVariant {
          id
          title
          metafield(key: "recommended_products", namespace: "custom") {
            value
            id
            reference
          }
        }
      }
    }
    `;
  
    const STOREFRONT_API_VERSION = "2024-04";
  
    const STOREFRONT_API_URL = `/api/${STOREFRONT_API_VERSION}/graphql.json`;
  
    /** @type {RequestInit} */
    const requestDefaults = {
      async: true,
      crossDomain: true,
      method: "POST",
      headers: {
        "X-Shopify-Storefront-Access-Token": import.meta.env.VITE_SHOPIFY_STOREFRONT_API_KEY,
        "Content-Type": "application/graphql",
      },
    };
  
    const fetchFromStorefront = (body) => {
      return fetch(STOREFRONT_API_URL, {
        ...requestDefaults,
        body
      }).then((response) => response.json());
    };
  
    try {
      const storeFrontVariants = await fetchFromStorefront(queryVariant);
      const variantProductData = storeFrontVariants.data;
      const variantMetafield = variantProductData.node.metafield;
  
      if (variantMetafield) {
        const variantMetaId = variantMetafield.value;
        const variantMetaQuery = `
        {
          metaobject(id: "${variantMetaId}") {
            handle
            id
            fields {
              key
              value
              reference
            }
          }
        }
        `;
  
        const variantMetaDetails = await fetchFromStorefront(variantMetaQuery);
        const variantMetaData = variantMetaDetails.data;
        const variantHeading = variantMetaData.metaobject.fields.find((field) => field.key === "heading")?.value || '';
  
        const variantIdsField = variantMetaData.metaobject.fields.find((field) => field.key === "variants");
        const productsField = variantMetaData.metaobject.fields.find((field) => field.key === "products");
  
        const variantIds = variantIdsField ? JSON.parse(variantIdsField.value) : [];
        const productIds = productsField ? JSON.parse(productsField.value) : [];
  
  
        if (!variantIds.length && !productIds.length) {
          return;
        }
  
        const variantQueries = variantIds.length > 0 ? variantIds.map((variant, index) => `
          q${index}: node(id: "${variant}") {
            ... on ProductVariant {
              id
              title
              quantityAvailable
              product {
                id
                handle
                title
                featuredImage {
                  url
                }
                options {
                  id
                  name
                  values
                }
              }
              selectedOptions {
                name
                value
              }
              price {
                amount
                currencyCode
              }
              image {
                src
              }
            }
          }
        `).join("\n") : '';
  
        const productQueries = productIds.length > 0 ? productIds.map((product, index) => `
          p${index}: node(id: "${product}") {
            ... on Product {
              id
              handle
              title
              featuredImage {
                url
              }
              options {
                name
                values
              }
              variants(first: 1) {
                edges {
                  node {
                    id
                    title
                    quantityAvailable
                    selectedOptions {
                      name
                      value
                    }
                    price {
                      amount
                      currencyCode
                    }
                    image {
                      src
                    }
                  }
                }
              }
            }
          }
        `).join("\n") : '';
  
        const metaQuery = `
        query getMetaInfo @inContext(country: ${currentCountry}) {
          ${variantQueries}
          ${productQueries}
        }
        `;
  
        const metaDetails = await fetchFromStorefront(metaQuery);
        const queriedMetaDetails = metaDetails.data;
  
        const variantRecommendationsArray = variantIds.length > 0 ? Object.values(queriedMetaDetails).filter(item => item.id.includes('/ProductVariant/')) : [];
        const productRecommendationsArray = productIds.length > 0 ? Object.values(queriedMetaDetails).filter(item => item.id.includes('/Product/')) : [];
  
        const productRecommendations = productRecommendationsArray.map(product => {
          const variant = product.variants.edges[0].node;
          const parentProductOptions = product.options.find(option => option.name.includes('Size'));
  
          let parentSizeOptions = [];
          if (parentProductOptions) {
            parentSizeOptions = parentProductOptions.values.map(sizeValue => {
              const parentIsOutOfStock = variant.selectedOptions.some(option => option.name === 'Size' && option.value === sizeValue) &&
                variant.quantityAvailable < 1;
  
              return {
                value: sizeValue,
                parentIsOutOfStock,
              };
            });
          }
  
          return {
            ...variant,
            product: {
              id: product.id,
              handle: product.handle,
              title: product.title,
              featuredImage: product.featuredImage,
              options: product.options,
            },
            parentSizeOptions,
          };
        });
  
        const variantRecommendations = variantRecommendationsArray.map((variant) => {
          const parentProductOptions = variant.product.options.find(option => option.name.includes('Size'));
  
          let parentSizeOptions = [];
          if (parentProductOptions) {
            parentSizeOptions = parentProductOptions.values.map(sizeValue => {
              const parentIsOutOfStock = variant.selectedOptions.some(option => option.name === 'Size' && option.value === sizeValue) &&
                variant.quantityAvailable < 1;
  
              return {
                value: sizeValue,
                parentIsOutOfStock,
              };
            });
          }
  
          return {
            ...variant,
            parentSizeOptions,
          };
        });
  
        const recommendations = [
          ...productRecommendationsArray.length > 0 ? productRecommendations : [],
          ...variantRecommendationsArray.length > 0 ? variantRecommendations : []
        ];
  
        await this.createAndMountApp(recommendations, variantHeading);
  
        return variantProductData;
  
      } else {
        const parentRecs = await this.getParentRecs(productId);
        if (parentRecs && parentRecs.recommendations && parentRecs.recommendations.length > 0) {
          await this.createAndMountApp(parentRecs.recommendations, parentRecs.productHeading);
        }
      }
    } catch (e) {
      console.error(e);
    }
  },
  
  onFormOptionChange(event) {
    const { variant } = event.dataset;
    const target = event.target;
    const isSizeOptionClick = target.closest('.product-variant--size');

    this.updatedVariant = variant;
    this.updateBackInStockForm(this.product, variant);
    if (this.product.metafields && this.product.metafields["custom.alternate_colours"] && !isSizeOptionClick) {
      this.debouncedUpdateSizeAltSnippet(variant);
    }
    this.updateAvailableVariants();
    this.productImages.update(event);
    this.updateColour(variant);
    this.updateSizes();
    this.updatePrice(variant);
    this.updateSubmitButton(variant);
    this.updateSku(variant);
    this.updateBarcode(variant);
    this.updateQuantitiesFromInventory(variant);
    this.disableProductVariantButton();
    let newProductId = variant.featured_image.product_id;
    this.getProductRecs(variant, newProductId);

    this.updateBrowserHistory(variant);

    if (this.$wishlistButton) {
      this.checkIfProductIsAlreadyInTheWishlist();
    }
  },

  showLoadingSpinner() {
    const sizeDiv = this.$el.querySelector('.product-variant--size');
    if (sizeDiv) {
      sizeDiv.classList.add(classes.loading);
        const spinner = document.createElement('div');
        spinner.classList.add('loading-spinner');
        sizeDiv.appendChild(spinner);
    }
  },

  hideLoadingSpinner() {
    const sizeDiv = this.$el.querySelector('.product-variant--size');
    if (sizeDiv) {
      sizeDiv.classList.remove(classes.loading);
        const spinner = sizeDiv.querySelector('.loading-spinner');
        if (spinner) {
          spinner.remove();
        }
    }
  },

  updateSizeAltSnippet(variant) {

    const form = this.$el;
  
    if (!form || !(form instanceof HTMLFormElement)) {
      return;
    }

    this.showLoadingSpinner();
    
    const formData = new FormData(form.element);
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(formData);

    searchParams.set('variant', variant.id);
    searchParams.set('product-id', this.product.id);
    url.search = searchParams.toString();
  
    fetch(url.toString(), { credentials: 'same-origin' })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSizeOptions = doc.querySelector(selectors.sizeAltSnippet);

        if (newSizeOptions) {
          const currentSizeOptions = this.$el.querySelector(selectors.sizeAltSnippet);
          if (currentSizeOptions) {
            currentSizeOptions.replaceWith(newSizeOptions);
            this.initializeProductForm();
          } else {
            console.error('Failed to find current size options to replace');
          }
        } else {
          console.error('Failed to find new size options in the fetched HTML');
        }
      })
      .catch(error => console.error(`Error fetching size options: ${error}`))
      .finally(() => {
        this.hideLoadingSpinner();
      });
  },
  
  initializeProductForm() {
    this.productForm = new ProductForm(this.$el, this.product, {
      onFormSubmit: this.onFormSubmit.bind(this),
      onOptionChange: this.onFormOptionChange.bind(this),
      onQuantityChange: this.onFormQuantityChange.bind(this),
    });
  },

  onFormSubmit(event) {
    const id = document.querySelector(selectors.form.id).value;
    this.$submit.innerText = window.translations.product.addingToCart;

    const properties = {
      retail_price: this.retailPrice,
      Message: "",
    };
    if (this.$giftCardMessage && this.$giftCardMessage.value.length > 0) {
      properties.Message = this.$giftCardMessage.value;
    }

    if (
      this.$giftCardRecipientEmail &&
      this.$giftCardRecipientEmail.value.length > 0 &&
      this.$giftCardRecipientName &&
      this.$giftCardRecipientName.value.length > 0
    ) {
      properties["__shopify_send_gift_card_to_recipient"] = "if_present";
      properties["Recipient email"] = this.$giftCardRecipientEmail.value;
    }
    if (
      this.$giftCardRecipientName &&
      this.$giftCardRecipientName.value.length > 0
    ) {
      properties["Recipient name"] = this.$giftCardRecipientName.value;
    }
    if (
      this.$giftCardRecipientSendOn &&
      this.$giftCardRecipientSendOn.value.length > 0
    ) {
      properties["Send on"] = this.$giftCardRecipientSendOn.value;
    }

    Cart.add([
      {
        id,
        quantity: this.quantity,
        properties,
      },
    ])
      .then((response) => response.json())
      .then((response) => {
        if (response.status === 422) {
          this.hideAddToCartButtons();
          this.showBackInStockButtons();
          this.$submit.innerText = window.translations.product.addToCart;
          throw Error(response.description);
        }
        const productData = response.items[0];
        const variantId = parseInt(id, 10);
        const variant = this.variants.find(v => v.id === variantId);
        const variantPrice = variant ? variant.price : null;

        window.dispatchEvent(
          new CustomEvent("cart.added", { detail: { productData, variantPrice } })
        );

        this.$submit.innerText = window.translations.product.addToCart;
        this.$addedToCartScreenReaderMessage.textContent = `Item added to cart`;
      })
      .catch((error) => {
        this.$el.dispatchEvent(
          new CustomEvent("cart.error", { detail: { error } })
        );
      });

    event.preventDefault();
  },

  updateColour(variant) {
    if (!variant) {
      return;
    }
    const index = this.product.options.findIndex(
      (option) => option.name === "Colour"
    );

    if (index === -1) {
      return;
    }
    const $colour = this.$el.querySelector(selectors.color);
    const colour = variant.options[index];

    $colour.innerText = colour;
  },

  disableProductVariantButton() {
    const colourOption = getColourOption();
    const unavailableProducts = this.product.variants
      .filter((variant) => variant.available === false)
      .map((product) => {
        return {
          color: product.option1,
          size: product.option2,
          size2: product.option3,
        };
      });

    const $size1s = document.querySelectorAll(
      '.product-variant[data-index="1"] [data-product-size]'
    );
    const $size2s = document.querySelectorAll(
      '.product-variant[data-index="2"] [data-product-size]'
    );

    if ($size1s.length > 0 && $size2s.length === 0) {
      $size1s.forEach(($size1) => {
        const productSize1 = $size1.dataset.productSize;

        // reset all size buttons
        $size1.classList.remove(classes.sizeDisabled);

        unavailableProducts.forEach((product) => {
          if (product.color === colourOption && product.size === productSize1) {
            $size1.classList.add(classes.sizeDisabled);
          }
        });
      });
    } else if ($size1s.length === 0 && $size2s.length > 0) {
      $size2s.forEach(($size2) => {
        const productSize2 = $size2.dataset.productSize;

        // reset all size buttons
        $size2.classList.remove(classes.sizeDisabled);

        unavailableProducts.forEach((product) => {
          if (product.color === colourOption && product.size === productSize2) {
            $size2.classList.add(classes.sizeDisabled);
          }
        });
      });
    } else if ($size1s.length > 0 && $size2s.length > 0) {
      /* Update Size2 relative to Size1 */
      $size1s.forEach(($size1) => {
        const productSize1 = $size1.dataset.productSize;

        // Check if size option is selected
        if ($size1.querySelectorAll("input:checked").length > 0) {
          $size2s.forEach(($size2) => {
            const productSize2 = $size2.dataset.productSize;

            // reset all size buttons
            $size2.classList.remove(classes.sizeDisabled);

            unavailableProducts.forEach((product) => {
              if (
                product.color === colourOption &&
                product.size === productSize1 &&
                product.size2 === productSize2
              ) {
                $size2.classList.add(classes.sizeDisabled);
              }
            });
          });
        }
      });

      /* Update Size1 relative to Size2 */
      $size2s.forEach(($size2) => {
        const productSize2 = $size2.dataset.productSize;

        // Check if size option is selected
        if ($size2.querySelectorAll("input:checked").length > 0) {
          $size1s.forEach(($size1) => {
            const productSize1 = $size1.dataset.productSize;

            // reset all size buttons
            $size1.classList.remove(classes.sizeDisabled);

            unavailableProducts.forEach((product) => {
              if (
                product.color === colourOption &&
                product.size === productSize1 &&
                product.size2 === productSize2
              ) {
                $size1.classList.add(classes.sizeDisabled);
              }
            });
          });
        }
      });
    }
  },

  updateSizes() {
    const variants = this.product.variants.map((variant) => {
      return {
        color: variant.option1,
        size: variant.option2,
        size2: variant.option3,
      };
    });

    this.$sizes.forEach((size) => {
      const sizeData = size.dataset.productSize;
      const color = document.querySelector(selectors.color).textContent.trim();
      const sizeOfVariant = variants.filter(
        (variant) => variant.color === color
      );
      const checkSizeAvailable = !sizeOfVariant.some(
        (item) => item.size === sizeData || item.size2 === sizeData
      );

      size.classList.toggle(classes.hide, checkSizeAvailable);
    });
  },

  updateAvailableVariants() {
    if (this.isAlternateColourProduct()) {
      this.updateAlternateColourVariants();
    } else {
      this.product.options.forEach(({ name }, index) => {
        if (name.includes("Size") || name.includes("Colour")) {
          return;
        }
        
        const variant = this.$el.querySelector(selectors.variant(index));
        const options = variant.querySelectorAll(selectors.option);
        const available = this.getAvailableValuesForOption(index);

        options.forEach((opt) => {
          const option = opt;

          option.innerText = available.includes(option.value)
            ? `${option.value} (${window.translations.product.unavailable})`
            : option.value;
        });
      });
    }
  },

  updateAlternateColourVariants() {
    const colourOption = getColourOption();
    const unavailableProducts = this.product.variants
        .filter((variant) => variant.available === false)
        .map((product) => {
            return {
                color: product.option1,
                size: product.option2,
                size2: product.option3,
            };
        });

    const $size1s = document.querySelectorAll('.product-variant[data-index="1"] [data-product-size]');
    const $size2s = document.querySelectorAll('.product-variant[data-index="2"] [data-product-size]');

    if ($size1s.length > 0 && $size2s.length === 0) {
      $size1s.forEach(($size1) => {
          const productSize1 = $size1.dataset.productSize;

          // reset all size buttons
          $size1.classList.remove(classes.sizeDisabled);

          unavailableProducts.forEach((product) => {
              if (product.color === colourOption && product.size === productSize1) {
                  $size1.classList.add(classes.sizeDisabled);
              }
          });
      });
    } else if ($size1s.length === 0 && $size2s.length > 0) {
      $size2s.forEach(($size2) => {
          const productSize2 = $size2.dataset.productSize;

          // reset all size buttons
          $size2.classList.remove(classes.sizeDisabled);

          unavailableProducts.forEach((product) => {
              if (product.color === colourOption && product.size === productSize2) {
                  $size2.classList.add(classes.sizeDisabled);
              }
          });
      });
    } else if ($size1s.length > 0 && $size2s.length > 0) {
      /* Update Size2 relative to Size1 */
      $size1s.forEach(($size1) => {
          const productSize1 = $size1.dataset.productSize;

          // Check if size option is selected
          if ($size1.querySelectorAll("input:checked").length > 0) {
              $size2s.forEach(($size2) => {
                  const productSize2 = $size2.dataset.productSize;

                  // reset all size buttons
                  $size2.classList.remove(classes.sizeDisabled);

                  unavailableProducts.forEach((product) => {
                      if (
                          product.color === colourOption &&
                          product.size === productSize1 &&
                          product.size2 === productSize2
                      ) {
                          $size2.classList.add(classes.sizeDisabled);
                      }
                  });
              });
          }
      });

      /* Update Size1 relative to Size2 */
      $size2s.forEach(($size2) => {
          const productSize2 = $size2.dataset.productSize;

          // Check if size option is selected
          if ($size2.querySelectorAll("input:checked").length > 0) {
              $size1s.forEach(($size1) => {
                  const productSize1 = $size1.dataset.productSize;

                  // reset all size buttons
                  $size1.classList.remove(classes.sizeDisabled);

                  unavailableProducts.forEach((product) => {
                      if (
                          product.color === colourOption &&
                          product.size === productSize1 &&
                          product.size2 === productSize2
                      ) {
                          $size1.classList.add(classes.sizeDisabled);
                      }
                  });
              });
          }
      });
    }
  },

  updatePrice(variant) {
    if (!variant) {
      return;
    }

    const $priceWrapper = this.$el.querySelector(selectors.priceWrapper);
    const $price = this.$el.querySelector(selectors.price);
    const $retailPrice = this.$el.querySelector("[data-retail-price]");
    const $compareAtPrice = this.$el.querySelector(selectors.compareAtPrice);
    let showRetailPrices = false;
    let showSalePrices = false;

    if (window.theme.customerType === "trade" && window.theme.isTradeStore) {
      showRetailPrices = true;
    }

    if (
      variant.compare_at_price !== null &&
      variant.compare_at_price > variant.price
    ) {
      showSalePrices = true;
    }

    /**
     * Retail Price
     *
     * @todo is keeping track of this necessary, anymore?
     *
     * We keep track of the retail price throughout the customer journey just in case the customer logs in as trade customer at a later step in the journey.
     * The first conditional statement checks to see if the trade price differs between variants, if it does the price is retrieved from an array stored in the window object.
     * If the trade price is the same across all variants we just use the trade price sourced from the liquid template and display the price to the customer.
     * Even though we are activally keeping track of the trade prices we don't show them to commercial customers
     */

    const variantRetailPrice = getRetailPrice(variant.id);
    if (typeof variantRetailPrice === "number") {
      $retailPrice.dataset.retailPrice = variantRetailPrice;
      this.retailPrice = variantRetailPrice;
    }
    if (this.retailPrice) {
      $retailPrice.innerHTML = `${formatMoney(
        this.retailPrice,
        theme.moneyFormat
      )}`;
    }
    if (showRetailPrices) {
      $price.innerText = formatMoney(variant.price, theme.moneyFormat);
      return;
    }

    /**
     * Sale Price
     * Trade customers should never see any sale prices.
     * If a trade user is logged in, the return statement on line 345 will end the function execution before the browser executes the sale logic.
     */

    if (showSalePrices) {
      $priceWrapper.classList.add(classes.discounted);
      $compareAtPrice.classList.remove(classes.hide);
      $compareAtPrice.innerText = formatMoney(
        variant.compare_at_price,
        theme.moneyFormat
      );
      $price.innerText = formatMoney(variant.price, theme.moneyFormat);
      this.reloadKlarna(variant.price);
      return;
    }

    // Show Standard Price
    $priceWrapper.classList.remove(classes.discounted);
    $compareAtPrice.classList.add(classes.hide);
    $priceWrapper.classList.toggle(classes.hide, !variant);
    $price.innerText = formatMoney(variant.price, theme.moneyFormat);
    this.reloadKlarna(variant.price);
  },

  updateSubmitButton(variant) {
    if (!variant.available) {
      this.showBackInStockButtons();
      this.hideAddToCartButtons();
    } else if (variant.available) {
      this.hideBackInStockButtons();
      this.showAddToCartButtons();
    }
  },

  showAddToCartButtons() {
    this.$el.querySelectorAll("[data-add-to-cart]").forEach((element) => {
      element.classList.remove("u-hide");
      element.disabled = false;
    });
  },

  hideAddToCartButtons() {
    this.$el.querySelectorAll("[data-add-to-cart]").forEach((element) => {
      element.classList.add("u-hide");
      element.disabled = true;
    });
  },

  showBackInStockButtons() {
    this.$el
      .querySelectorAll('[data-micromodal-trigger="back-in-stock"]')
      .forEach((element) => {
        element.classList.remove("u-hide");
        element.disabled = false;
      });
  },

  hideBackInStockButtons() {
    this.$el
      .querySelectorAll('[data-micromodal-trigger="back-in-stock"]')
      .forEach((element) => {
        element.classList.add("u-hide");
        element.disabled = true;
      });
  },

  updateSku(variant) {
    const $skuWrapper = this.$el.querySelector(selectors.skuWrapper);
    const $sku = this.$el.querySelector(selectors.sku);

    if (variant) {
      $skuWrapper.classList.toggle(classes.hide, !variant.sku);
      $sku.innerText = variant.sku;
    } else {
      $skuWrapper.classList.add(classes.hide);
    }
  },

  updateBarcode(variant) {
    const $barcodeWrapper = this.$el.querySelector(selectors.barcodeWrapper);
    const $barcode = this.$el.querySelector(selectors.barcode);

    if ($barcode === null) {
      return;
    }

    if (variant) {
      $barcodeWrapper.classList.toggle(classes.hide, !variant.barcode);
      $barcode.innerText = variant.barcode;
    } else {
      $barcodeWrapper.classList.add(classes.hide);
    }
  },

  updateBrowserHistory(variant) {
    if (!variant || !this.enableHistory) {
      return;
    }

    const path = getUrlWithVariant(window.location.href, variant.id);

    window.history.replaceState({ path }, "", path);
  },

  checkIfVideoHasLoaded(video) {
    const iframe = video.querySelector("iframe");
    iframe.addEventListener("load", () => {
      video.classList.add("iframe-loaded");
    });
  },

  reloadKlarna(price) {
    const $message = document.querySelector("klarna-placement");
    if ($message) {
      document.querySelector("klarna-placement").dataset.purchaseAmount = price;
      window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
      window.KlarnaOnsiteService.push({
        eventName: "refresh-placements",
      });
    }
  },
});
